import React, { useEffect } from "react";

import { ResponsiveModal } from "@UI/Components/ResponsiveModal/ResponsiveModal";
import { HStack, VStack } from "@UI/Components/Stack/Stack";
import { Heading } from "@UI/Components/Heading/Heading";
import { Text } from "@UI/Components/Text/Text";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import { Button } from "@UI/Components/Button/Button";
import { Link } from "@UI/Components/Link/Link";
import { Image } from "@UI/Components/Image/Image";
import { AlertBox } from "@UI/Components/AlertBox/AlertBox";
import { Icon } from "@UI/Components/Icon/Icon";
import messages from "./messages";
import { IConfirmationEmailTopProps } from "./ConfirmationEmailTop";
import { useToast, ToastProps } from "@UI/hooks/useToast";
import { v4 as uuidv4 } from "uuid";
import { Xmark } from "@UI/Components/Icons/Xmark";
import useMediaQuery from "@UI/hooks/useMediaQuery";
import { useResendEmail } from "@modules/Authentication/hooks/useResendEmail";

interface IEmailConfirmationInstructionModalProps
  extends IConfirmationEmailTopProps {
  isOpen: boolean;
  onClose: () => void;
  modifyEmail: () => void;
}
export const EmailConfirmationInstructionModal = (
  props: IEmailConfirmationInstructionModalProps
) => {
  const { email, isOpen, onClose, modifyEmail } = props;

  const toast = useToast();
  const { isMobile } = useMediaQuery();

  const showToast = (toastData: Pick<ToastProps, "status" | "description">) => {
    const { status, description } = toastData;
    const toastId = uuidv4();
    toast({
      id: toastId,
      status,
      position: isMobile ? "bottom" : "top-right",
      duration: 2000,
      isClosable: true,
      render: () => (
        <AlertBox
          status={status}
          background="white"
          childrenContainerStyle={{ justifyContent: "start" }}
          marginTop={!isMobile ? "80px" : "initial"}
        >
          <HStack justifyContent="space-between" flexGrow={1}>
            <Text m="0" ml={3} color="gray.700">
              {description}
            </Text>
            <Icon
              as={Xmark}
              color="gray.700"
              cursor="pointer"
              onClick={() => toast.close(toastId)}
            />
          </HStack>
        </AlertBox>
      ),
    });
  };

  const { resendEmail, isLoading, isSuccess } = useResendEmail(showToast);

  useEffect(() => {
    isSuccess && onClose();
  }, [isSuccess]);

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={isLoading ? () => null : onClose}
      closeButtonDisabled={true}
      closeOnOverlayClick={isLoading ? false : true}
    >
      <VStack spacing={"36px"} w="full" textAlign="center" mb="12px">
        <Image
          src={require(`assets/images/email-confirmation.svg`)}
          alt={"email-confirmation"}
        />
        <VStack spacing="12px" w="full">
          <Heading as="h4" fontSize="2xl" sx={{ "text-wrap": "balance" }}>
            <FormattedMessage
              {...messages["confirmationEmailTop.modal.header"]}
            />
          </Heading>
          <Text fontSize="md">
            <FormattedMessage
              {...messages["confirmationEmailTop.modal.emailSentOn"]}
              values={{
                date: new Date().toDateString().toString(),
                email,
              }}
            />
          </Text>
          <VStack spacing="0">
            <Text fontSize="md">
              <FormattedMessage
                {...messages[
                  "confirmationEmailTop.modal.isNotYourUsualEmailAddress"
                ]}
              />
            </Text>
            <Link
              h="fit-content"
              color="pink.500"
              _hover={{ color: "pink.700" }}
              onClick={modifyEmail}
            >
              <FormattedMessage
                {...messages["confirmationEmailTop.modal.modifyEmailLinkText"]}
              />
            </Link>
          </VStack>
        </VStack>
        <VStack spacing="12px" w="full">
          <Button
            w="inherit"
            variant="outline"
            size="lg"
            colorScheme="seaGreen"
            boxShadow="none"
            isDisabled={isLoading}
            isLoading={isLoading}
            onClick={() => resendEmail(email)}
          >
            <FormattedMessage
              {...messages["confirmationEmailTop.modal.resendEmailCTAText"]}
            />
          </Button>
          <Text fontSize="sm" color="gray.500">
            <FormattedMessage
              {...messages["confirmationEmailTop.modal.footerText"]}
            />
          </Text>
        </VStack>
      </VStack>
    </ResponsiveModal>
  );
};
